import * as Dialog from '@radix-ui/react-dialog';
import { Save, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { ModalEditarParteProps } from './types';
import { useModalState } from './hooks/useModalState';
import { useImageUpload } from './hooks/useImageUpload';
import { useIAAnalysis } from './hooks/useIAAnalysis';
import { useModalInitialization } from './hooks/useModalInitialization';
import { calcularHRN } from './utils/hrn-calculator';
import { ModalHeader } from './components/ModalHeader';
import { ImageUploadSection } from './components/ImageUploadSection';
import { FotosCampoGaleria } from '@/components/apreciacao/fotos-campo-galeria';
import { imagemParaDataUrl } from '@/utils/imagem-data-url';
import { CicloVidaSection } from './components/CicloVidaSection';
import { TiposPerigoSection } from './components/TiposPerigoSection';
import { AdequacoesTecnicasSection } from './components/AdequacoesTecnicasSection';
import { AdequacoesAdministrativasSection } from './components/AdequacoesAdministrativasSection';
import { HrnTablesSection } from './components/HrnTablesSection';
import { SrpcsSection } from './components/SrpcsSection';
import { StatusProntaSection } from './components/StatusProntaSection';
import { IADialog } from './components/IADialog';
import { ChecklistRevisao } from '@/components/apreciacao/checklist-revisao';
import { useI18n } from '@/contexts/i18n-hook';

export function ModalEditarParte({
  aberto,
  parte,
  riscos,
  numeroParte,
  onFechar,
  onSalvar,
  descricaoMaquina = '',
  apreciacaoId,
  fotosCampo = [],
  fotosCampoUsadasUrls = [],
  pictogramas = [],
}: ModalEditarParteProps) {
  const { t } = useI18n();
  const state = useModalState(parte, descricaoMaquina);
  const [erroSalvamento, setErroSalvamento] = useState<string | null>(null);
  const [erroImagem, setErroImagem] = useState<string | null>(null);
  // Aba do painel lateral: fotos capturadas em campo ou pictogramas padrão.
  const [abaLateral, setAbaLateral] = useState<'fotos' | 'pictogramas'>('fotos');

  const {
    handleUploadImagem,
    handlePasteImagem,
  } = useImageUpload(
    t,
    aberto,
    state.fotoParte1,
    state.fotoParte2,
    state.fotoParte3,
    state.fotoParte4,
    state.setFotoParte1,
    state.setFotoParte2,
    state.setFotoParte3,
    state.setFotoParte4
  );

  const inserirImagemNoProximoSlot = (valor: string): boolean => {
    const ocupados = [state.fotoParte1, state.fotoParte2, state.fotoParte3, state.fotoParte4];
    const setters = [state.setFotoParte1, state.setFotoParte2, state.setFotoParte3, state.setFotoParte4];
    const idx = ocupados.findIndex((f) => !f);
    if (idx === -1) {
      setErroImagem(t('apreciacaoModais.editarParte.imagens.erroSlotsCheios'));
      return false;
    }
    setters[idx](valor);
    setErroImagem(null);
    return true;
  };

  // Adiciona foto selecionada da galeria como data URL base64, igual às imagens
  // enviadas do PC. Isso evita depender de /uploads/... no backend ao gerar PDF.
  const adicionarFotoCampo = async (valor: string) => {
    if (valor.startsWith('data:image/')) {
      inserirImagemNoProximoSlot(valor);
      return;
    }

    try {
      const dataUrl = await imagemParaDataUrl(valor);
      inserirImagemNoProximoSlot(dataUrl);
    } catch (error) {
      console.error('Erro ao embutir foto selecionada:', error);
      setErroImagem(t('apreciacaoModais.editarParte.imagens.erroEmbutirFoto'));
    }
  };

  // Pictograma: busca o arquivo ORIGINAL (qualidade cheia, sem ?w&q) e insere como
  // base64 — mesmo caminho das fotos coladas, preservando a qualidade da pasta e
  // garantindo que o backend embuta o pictograma no PDF (a URL /api/pictogramas/...
  // não é baixada na geração). Em caso de falha, cai para a URL original.
  const adicionarPictograma = async (url: string) => {
    try {
      const dataUrl = await imagemParaDataUrl(url);
      inserirImagemNoProximoSlot(dataUrl);
    } catch {
      inserirImagemNoProximoSlot(url);
    }
  };

  const { handleAnalisarComIA } = useIAAnalysis(
    t,
    state.fotoParte1,
    state.descricaoMaquinaIA,
    state.nomeParte,
    parte,
    state.descricaoCondicaoAtual,
    state.perigosEncontrados,
    state.potenciaisConsequencias,
    state.riscosEditados,
    state.setNomeParte,
    state.setCiclosVida,
    state.setEventoPerigoso,
    state.setTiposPerigoSelecionados,
    state.setPotenciaisConsequencias,
    state.setPerigosEncontrados,
    state.setConforme,
    state.setDescricaoCondicaoAtual,
    state.setMedidasControle,
    state.setExigenciasNormativas,
    state.setAdequacoesNormativas,
    state.setRiscosEditados,
    state.setMostrarDialogIA,
    state.setAnalisandoIA
  );

  useModalInitialization(
    aberto,
    parte,
    riscos,
    descricaoMaquina,
    state.setNomeParte,
    state.setFotoParte1,
    state.setFotoParte2,
    state.setFotoParte3,
    state.setFotoParte4,
    state.setCiclosVida,
    state.setEventoPerigoso,
    state.setConforme,
    state.setTiposPerigoSelecionados,
    state.setPerigosEncontrados,
    state.setPotenciaisConsequencias,
    state.setPotenciaisConsequenciasEspecificas,
    state.setOrigemEspecifica,
    state.setDescricaoCondicaoAtual,
    state.setMedidasControle,
    state.setExigenciasNormativas,
    state.setAdequacoesNormativas,
    state.setPronta,
    state.setRiscosEditados,
    state.setDescricaoMaquinaIA,
    state.setPartesCorpoAtingidas,
    state.setExibirHRN,
    state.setExibirSRPCS
  );

  const handleSalvar = async () => {
    setErroSalvamento(null);

    // Validação do nome da parte
    if (!state.nomeParte?.trim()) {
      setErroSalvamento(t('apreciacaoModais.editarParte.erroNomeObrigatorio'));
      return;
    }

    state.setSalvando(true);
    try {
      // Recalcular todos os HRNs antes de salvar para garantir que estão corretos
      const riscosComHRNRecalculado = state.riscosEditados.map(risco => {
        // Recalcular HRN NA CONDIÇÃO ATUAL DA MÁQUINA
        const poAtual = (risco.probabilidadeAtual != null && !isNaN(Number(risco.probabilidadeAtual)) && Number(risco.probabilidadeAtual) > 0) ? Number(risco.probabilidadeAtual) : 1;
        const feAtual = (risco.frequenciaAtual != null && !isNaN(Number(risco.frequenciaAtual)) && Number(risco.frequenciaAtual) > 0) ? Number(risco.frequenciaAtual) : 1;
        const gsAtual = (risco.gravidadeAtual != null && !isNaN(Number(risco.gravidadeAtual)) && Number(risco.gravidadeAtual) > 0) ? Number(risco.gravidadeAtual) : 1;
        const npAtual = (risco.exposicaoAtual != null && !isNaN(Number(risco.exposicaoAtual)) && Number(risco.exposicaoAtual) > 0) ? Number(risco.exposicaoAtual) : 1;
        const hrnAtualRecalculado = calcularHRN(poAtual, feAtual, gsAtual, npAtual);

        // Recalcular HRN futuro
        const probFuturo = risco.probabilidadeFuturo ?? risco.probabilidadeAtual;
        const freqFuturo = risco.frequenciaFuturo ?? risco.frequenciaAtual;
        const gravFuturo = risco.gravidadeFuturo ?? risco.gravidadeAtual;
        const expFuturo = risco.exposicaoFuturo ?? risco.exposicaoAtual;
        const poFuturo = (probFuturo != null && !isNaN(Number(probFuturo)) && Number(probFuturo) > 0) ? Number(probFuturo) : 1;
        const feFuturo = (freqFuturo != null && !isNaN(Number(freqFuturo)) && Number(freqFuturo) > 0) ? Number(freqFuturo) : 1;
        const gsFuturo = (gravFuturo != null && !isNaN(Number(gravFuturo)) && Number(gravFuturo) > 0) ? Number(gravFuturo) : 1;
        const npFuturo = (expFuturo != null && !isNaN(Number(expFuturo)) && Number(expFuturo) > 0) ? Number(expFuturo) : 1;
        const hrnFuturoRecalculado = calcularHRN(poFuturo, feFuturo, gsFuturo, npFuturo);

        return {
          ...risco,
          hrnAtual: hrnAtualRecalculado,
          hrnFuturo: hrnFuturoRecalculado,
          exibirHRN: state.exibirHRN,
          exibirSRPCS: state.exibirSRPCS,
        };
      });

      // Remover duplicações dos tipos principais
      const tiposPerigoUnicos = new Set<string>();
      state.tiposPerigoSelecionados.forEach(tp => {
        if (tp && tp.trim()) {
          tiposPerigoUnicos.add(tp.trim());
        }
      });

      // Remover duplicações dos perigos encontrados (case-insensitive)
      const perigosEncontradosFiltrados = new Set<string>();
      state.perigosEncontrados.forEach(pe => {
        const peTrimmed = pe.trim();
        if (peTrimmed && peTrimmed !== 'teste') {
          const peLower = peTrimmed.toLowerCase();
          // Garantir que não estamos adicionando tipos principais
          if (peLower !== 'mecânico' && peLower !== 'mecanico' &&
            peLower !== 'elétrico' && peLower !== 'eletrico' &&
            peLower !== 'ergonômico' && peLower !== 'ergonomico') {
            // Verificar se já não existe (case-insensitive)
            const jaExiste = Array.from(perigosEncontradosFiltrados).some(
              existente => existente.toLowerCase() === peLower
            );
            if (!jaExiste) {
              perigosEncontradosFiltrados.add(peTrimmed);
            }
          }
        }
      });

      const tiposPerigoFinal = Array.from(tiposPerigoUnicos);
      const perigosEncontradosFinal = Array.from(perigosEncontradosFiltrados);

      // Incluir qualquer texto pendente nos inputs antes de salvar
      const descricaoFinal = state.pendingDescricao.trim()
        ? [...state.descricaoCondicaoAtual, state.pendingDescricao.trim()]
        : state.descricaoCondicaoAtual;
      const medidasFinal = state.pendingMedida.trim()
        ? [...state.medidasControle, state.pendingMedida.trim()]
        : state.medidasControle;
      const exigenciasFinal = state.pendingExigencia.trim()
        ? [...state.exigenciasNormativas, state.pendingExigencia.trim()]
        : state.exigenciasNormativas;
      const adequacoesFinal = state.pendingAdequacao.trim()
        ? [...state.adequacoesNormativas, state.pendingAdequacao.trim()]
        : state.adequacoesNormativas;

      await onSalvar({
        parte: state.nomeParte,
        parteAntiga: parte,
        riscos: riscosComHRNRecalculado,
        fotoParte1: state.fotoParte1,
        fotoParte2: state.fotoParte2,
        fotoParte3: state.fotoParte3,
        fotoParte4: state.fotoParte4,
        ciclosVida: state.ciclosVida,
        eventoPerigoso: state.eventoPerigoso,
        conforme: state.conforme,
        tiposPerigo: tiposPerigoFinal, // Apenas tipos principais (Mecânico, Elétrico, Ergonômico)
        perigosEncontrados: perigosEncontradosFinal, // Outros perigos encontrados (separado)
        potenciaisConsequencias: state.potenciaisConsequencias,
        potenciaisConsequenciasEspecificas: state.potenciaisConsequenciasEspecificas,
        origemEspecifica: state.origemEspecifica,
        partesCorpoAtingidas: state.partesCorpoAtingidas,
        descricaoCondicaoAtual: descricaoFinal,
        medidasControle: medidasFinal,
        exigenciasNormativas: exigenciasFinal,
        adequacoesNormativas: adequacoesFinal,
        pronta: state.pronta,
        exibirHRN: state.exibirHRN,
        exibirSRPCS: state.exibirSRPCS,
      });
      onFechar();
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      setErroSalvamento(error?.message || t('apreciacaoModais.editarParte.erroSalvar'));
    } finally {
      state.setSalvando(false);
    }
  };

  const numeroFormatado = String(numeroParte).padStart(3, '0');

  return (
    <Dialog.Root open={aberto} onOpenChange={onFechar}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl w-[95vw] ${
            fotosCampo.length > 0 || pictogramas.length > 0 ? 'max-w-[1500px]' : 'max-w-6xl'
          } max-h-[90vh] overflow-hidden z-50 flex`}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="flex-1 min-w-0 overflow-y-auto">
          <div className="p-6">
            <ModalHeader
              numeroFormatado={numeroFormatado}
              nomeParte={state.nomeParte}
              setNomeParte={state.setNomeParte}
            />

            {/* Os comentários que o cliente fez NESTA parte, à vista de quem
                está justamente mexendo nela. Some quando não há revisão aberta. */}
            {apreciacaoId && parte && (
              <div className="mb-6">
                <ChecklistRevisao apreciacaoId={apreciacaoId} filtrarParte={parte} />
              </div>
            )}

            <div className="space-y-6">
              <ImageUploadSection
                fotoParte1={state.fotoParte1}
                fotoParte2={state.fotoParte2}
                fotoParte3={state.fotoParte3}
                fotoParte4={state.fotoParte4}
                analisandoIA={state.analisandoIA}
                setFotoParte1={state.setFotoParte1}
                setFotoParte2={state.setFotoParte2}
                setFotoParte3={state.setFotoParte3}
                setFotoParte4={state.setFotoParte4}
                setMostrarDialogIA={state.setMostrarDialogIA}
                handleUploadImagem={(file, indice) => handleUploadImagem(file, indice, setErroImagem)}
                handlePasteImagem={handlePasteImagem}
                erroImagem={erroImagem}
                onErroImagem={setErroImagem}
              />

              <CicloVidaSection
                ciclosVida={state.ciclosVida}
                novoCicloVida={state.novoCicloVida}
                setCiclosVida={state.setCiclosVida}
                setNovoCicloVida={state.setNovoCicloVida}
              />

              <TiposPerigoSection
                tiposPerigoSelecionados={state.tiposPerigoSelecionados}
                eventoPerigoso={state.eventoPerigoso}
                conforme={state.conforme}
                perigosEncontrados={state.perigosEncontrados}
                potenciaisConsequencias={state.potenciaisConsequencias}
                potenciaisConsequenciasEspecificas={state.potenciaisConsequenciasEspecificas}
                origemEspecifica={state.origemEspecifica}
                partesCorpoAtingidas={state.partesCorpoAtingidas}
                setTiposPerigoSelecionados={state.setTiposPerigoSelecionados}
                setEventoPerigoso={state.setEventoPerigoso}
                setConforme={state.setConforme}
                setPerigosEncontrados={state.setPerigosEncontrados}
                setPotenciaisConsequencias={state.setPotenciaisConsequencias}
                setPotenciaisConsequenciasEspecificas={state.setPotenciaisConsequenciasEspecificas}
                setOrigemEspecifica={state.setOrigemEspecifica}
                setPartesCorpoAtingidas={state.setPartesCorpoAtingidas}
              />

              <AdequacoesTecnicasSection
                descricaoCondicaoAtual={state.descricaoCondicaoAtual}
                medidasControle={state.medidasControle}
                exigenciasNormativas={state.exigenciasNormativas}
                setDescricaoCondicaoAtual={state.setDescricaoCondicaoAtual}
                setMedidasControle={state.setMedidasControle}
                setExigenciasNormativas={state.setExigenciasNormativas}
                pendingDescricao={state.pendingDescricao}
                setPendingDescricao={state.setPendingDescricao}
                pendingMedida={state.pendingMedida}
                setPendingMedida={state.setPendingMedida}
                pendingExigencia={state.pendingExigencia}
                setPendingExigencia={state.setPendingExigencia}
              />

              <AdequacoesAdministrativasSection
                adequacoesNormativas={state.adequacoesNormativas}
                setAdequacoesNormativas={state.setAdequacoesNormativas}
                pendingAdequacao={state.pendingAdequacao}
                setPendingAdequacao={state.setPendingAdequacao}
              />

              <HrnTablesSection
                riscosEditados={state.riscosEditados}
                setRiscosEditados={state.setRiscosEditados}
                exibirHRN={state.exibirHRN}
                setExibirHRN={state.setExibirHRN}
              />

              <SrpcsSection
                riscosEditados={state.riscosEditados}
                setRiscosEditados={state.setRiscosEditados}
                exibirSRPCS={state.exibirSRPCS}
                setExibirSRPCS={state.setExibirSRPCS}
              />

              <StatusProntaSection
                pronta={state.pronta}
                setPronta={state.setPronta}
              />
            </div>

            {/* Footer */}
            <div className="pt-6 border-t border-gray-200 mt-6">
              {erroSalvamento && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{erroSalvamento}</span>
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={onFechar}
                  disabled={state.salvando}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('comum.acao.cancelar')}
                </button>
                <button
                  onClick={handleSalvar}
                  disabled={state.salvando}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {state.salvando ? t('comum.acao.salvando') : t('apreciacaoModais.editarParte.botaoSalvar')}
                </button>
              </div>
            </div>
          </div>
          </div>

          {(fotosCampo.length > 0 || pictogramas.length > 0) && (() => {
            const temFotos = fotosCampo.length > 0;
            const temPictogramas = pictogramas.length > 0;
            // Aba efetiva: se uma das origens não existe, força a outra.
            const aba = !temFotos ? 'pictogramas' : !temPictogramas ? 'fotos' : abaLateral;
            return (
              <aside className="hidden md:flex md:flex-col w-72 xl:w-80 shrink-0 border-l border-gray-200 bg-gray-50">
                <div className="flex border-b border-gray-200">
                  {temFotos && (
                    <button
                      type="button"
                      onClick={() => setAbaLateral('fotos')}
                      className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 ${
                        aba === 'fotos'
                          ? 'border-schmersal-blue text-schmersal-blue'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {t('apreciacaoModais.editarParte.painel.abaFotos')}
                    </button>
                  )}
                  {temPictogramas && (
                    <button
                      type="button"
                      onClick={() => setAbaLateral('pictogramas')}
                      className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 ${
                        aba === 'pictogramas'
                          ? 'border-schmersal-blue text-schmersal-blue'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {t('apreciacaoModais.editarParte.painel.abaPictogramas')}
                    </button>
                  )}
                </div>
                <p className="px-3 pt-2 text-xs text-gray-500">{t('apreciacaoModais.editarParte.painel.dica')}</p>
                <div className="flex-1 overflow-y-auto p-3">
                  {aba === 'fotos' ? (
                    <FotosCampoGaleria
                      fotos={fotosCampo}
                      onSelecionar={adicionarFotoCampo}
                      usadasUrls={fotosCampoUsadasUrls}
                      compact
                    />
                  ) : (
                    <FotosCampoGaleria
                      fotos={pictogramas}
                      onSelecionar={adicionarPictograma}
                      compact
                    />
                  )}
                </div>
              </aside>
            );
          })()}
        </Dialog.Content>
      </Dialog.Portal>

      <IADialog
        mostrarDialogIA={state.mostrarDialogIA}
        setMostrarDialogIA={state.setMostrarDialogIA}
        descricaoMaquinaIA={state.descricaoMaquinaIA}
        setDescricaoMaquinaIA={state.setDescricaoMaquinaIA}
        analisandoIA={state.analisandoIA}
        handleAnalisarComIA={handleAnalisarComIA}
      />
    </Dialog.Root>
  );
}
